/**
 * useSaveWorkbench Hook
 * Handles saving workbench changes to Spotify playlist
 * Uses a full replace strategy: remove all tracks, then add new tracks in order
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAddTracks, useRemoveTracks, playlistKeys } from '../api/hooks/usePlaylists';
import { useWorkbenchStore } from '../stores/workbench';
import { toast } from '../components/ui/Toast';
import { getApiClient } from '../api/client';
import type { Track, PlaylistDetail } from '../api/types';

interface SaveWorkbenchParams {
  playlistId: string;
  tracks: Track[];
}

/**
 * Calculate the diff and generate save operations
 * Strategy: Full replace (remove all, add all in order)
 * This ensures correct ordering without complex position management
 */
function calculateSaveOperations(tracks: Track[]) {
  return {
    urisToAdd: tracks.map((track) => track.uri),
    needsFullReplace: true,
  };
}

/**
 * Hook to save workbench changes to Spotify
 * Returns a mutation that orchestrates remove + add operations
 */
export function useSaveWorkbench() {
  const { markClean, resetHistory } = useWorkbenchStore();
  const queryClient = useQueryClient();

  const addTracksMutation = useAddTracks();
  const removeTracksMutation = useRemoveTracks();

  return useMutation({
    mutationFn: async ({ playlistId, tracks }: SaveWorkbenchParams) => {
      const { urisToAdd } = calculateSaveOperations(tracks);

      // Strategy: Remove ALL current tracks from Spotify, then add workbench tracks
      // CRITICAL: Must fetch current tracks from Spotify to ensure complete replacement
      const api = getApiClient();

      // Step 1: Fetch ALL current tracks from Spotify playlist
      const currentPlaylist = await api.get<PlaylistDetail>(`/playlists/${playlistId}`);
      const currentUris = currentPlaylist.tracks.items.map((item) => item.track.uri);

      // Step 2: Remove ALL tracks from Spotify playlist
      if (currentUris.length > 0) {
        await removeTracksMutation.mutateAsync({
          playlistId,
          data: { uris: currentUris },
        });
      }

      // Step 3: Add all workbench tracks in the new order (position 0 = start of playlist)
      if (urisToAdd.length > 0) {
        await addTracksMutation.mutateAsync({
          playlistId,
          data: { uris: urisToAdd, position: 0 },
        });
      }

      return { success: true, trackCount: urisToAdd.length };
    },
    onSuccess: (result, variables) => {
      // CRITICAL: Update activePlaylist to reflect saved state
      // This prevents duplication on next save
      const store = useWorkbenchStore.getState();
      if (store.activePlaylist) {
        const updatedPlaylist = {
          ...store.activePlaylist,
          tracks: {
            total: variables.tracks.length,
            items: variables.tracks.map((track) => ({
              track,
              addedAt: new Date().toISOString(),
              addedBy: store.activePlaylist!.owner,
            })),
          },
        };

        // Update activePlaylist WITHOUT resetting originalTracks
        // We use setState directly to avoid setActivePlaylist's side effects
        useWorkbenchStore.setState({
          activePlaylist: updatedPlaylist,
        });
      }

      // Mark as clean and reset undo history since we synced to Spotify
      markClean();
      resetHistory();

      // Invalidate playlists list to update track count in sidebar
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });

      toast.success(
        `Playlist saved successfully! ${result.trackCount} track${result.trackCount !== 1 ? 's' : ''} synced to Spotify.`
      );
    },
    onError: (error: Error) => {
      const errorMessage = error.message || 'Unknown error occurred';
      toast.error(`Failed to save playlist: ${errorMessage}`);
      console.error('Save workbench error:', error);
    },
  });
}

/**
 * Hook to check if save is needed and get save status
 */
export function useSaveStatus() {
  const isDirty = useWorkbenchStore((state) => state.isDirty);
  const tracks = useWorkbenchStore((state) => state.tracks);
  const activePlaylist = useWorkbenchStore((state) => state.activePlaylist);
  const capabilities = useWorkbenchStore((state) => state.capabilities);

  const hasChanges = isDirty;
  const canSave = hasChanges && activePlaylist !== null && tracks.length >= 0 && capabilities.canSave;

  return {
    hasChanges,
    canSave,
    trackCount: tracks.length,
  };
}
